import momentTZ from 'moment-timezone';
import { i18n } from '@/main';
import vuex from '@/store';

export const EXPIRATION_WARNING_DAYS = 7;

const DATE_FORMAT = 'YYYY-MM-DD';

/*
  How much further away than the schedule's usual cadence a lone trailing date has to sit
  before it counts as isolated rather than as the next occurrence of the pattern.
*/
const ISOLATION_GAP_FACTOR = 2;

export type ScheduleExpirationStatus = 'normal' | 'expiring_soon' | 'expired' | 'none';

export interface ScheduleExpiration {
  end_date: string | null;
  days_remaining: number | null;
  status: ScheduleExpirationStatus;
}

export interface ExpiringSchedule {
  id: string;
  name: string;
  expiration: ScheduleExpiration;
}

/** Today in the project's timezone, so "days remaining" matches the user's calendar. */
function getProjectToday(timezone?: string) {
  const project_timezone = timezone || (vuex.state as any).Global.timezone;
  const now = project_timezone ? momentTZ().tz(project_timezone) : momentTZ();
  return momentTZ(now.format(DATE_FORMAT), DATE_FORMAT);
}

/** Unique, valid schedule dates in ascending order, whatever order `curves` came in. */
function getScheduleDates(curves: any): string[] {
  if (!Array.isArray(curves)) return [];

  const dates = curves
    .map((day_curve) => day_curve && day_curve.date)
    .filter((date) => typeof date === 'string' && momentTZ(date, DATE_FORMAT, true).isValid());

  return [...new Set<string>(dates)].sort();
}

function getDaysBetween(from_date: string, to_date: string): number {
  return momentTZ(to_date, DATE_FORMAT).diff(momentTZ(from_date, DATE_FORMAT), 'days');
}

function getMedian(values: number[]): number {
  const sorted = [...values].sort((value1, value2) => value1 - value2);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

/*
  Runs of consecutive days, which is the shape a schedule is built in: a daily schedule is
  one long run, Sun-Thu gives runs of five, a weekly schedule runs of one. Reading the
  pattern as runs keeps the judgement local - the gaps of a cadence the schedule used
  months ago cannot vouch for a date parked at the end of it.
*/
function getDateRuns(dates: string[]): string[][] {
  const runs: string[][] = [];

  dates.forEach((date, index) => {
    const previous_date = dates[index - 1];
    if (previous_date && getDaysBetween(previous_date, date) === 1) {
      runs[runs.length - 1].push(date);
    }else runs.push([date]);
  });

  return runs;
}

/** Days between the end of each run and the start of the next one. */
function getRunGaps(runs: string[][]): number[] {
  return runs.slice(1).map((run, index) => {
    const previous_run = runs[index];
    return getDaysBetween(previous_run[previous_run.length - 1], run[0]);
  });
}

/**
 * Whether the schedule ends on a single date that does not belong to the pattern before
 * it. Only a lone date can be isolated - a run of days at the end is a period of the
 * schedule in its own right, even a short one.
 */
function hasIsolatedLastDate(runs: string[][]): boolean {
  if (runs.length < 2) return false;

  const last_run = runs[runs.length - 1];
  if (last_run.length > 1) return false;

  // The pattern comes in runs of days and this date is on its own, so it is not one of them.
  const previous_run = runs[runs.length - 2];
  if (previous_run.length > 1) return true;

  /*
    A schedule of single dates (weekly, monthly, selected weekdays): the date is isolated
    only if it sits well beyond the cadence of the others. Two gaps is the least that can
    establish a cadence to compare against.
  */
  const gaps = getRunGaps(runs);
  const cadence_gaps = gaps.slice(0, -1);
  if (cadence_gaps.length < 2) return false;

  return gaps[gaps.length - 1] > getMedian(cadence_gaps) * ISOLATION_GAP_FACTOR;
}

/**
 * End of the schedule's recurring/continuous period - not simply the highest date in
 * `curves`: an isolated date at the end does not extend the expiration date.
 */
export function getScheduleEndDate(curves: any): string | null {
  const dates = getScheduleDates(curves);
  if (!dates.length) return null;

  let runs = getDateRuns(dates);
  while (hasIsolatedLastDate(runs)) runs = runs.slice(0, -1);

  const last_run = runs[runs.length - 1];
  return last_run[last_run.length - 1];
}

/**
 * Only active schedules are monitored for expiration: an inactive schedule is not running,
 * so counting down to its end date would warn about something that cannot happen anyway.
 * `active` is stored as 0/1 by the schedules page, hence the truthiness check - a schedule
 * with no `active` field at all is already presented as inactive by the page's checkbox.
 */
export function isScheduleActive(schedule: any): boolean {
  return !!(schedule && schedule.active);
}

/**
 * Shared expiration calculation: eligibility -> schedule curves -> end date -> days
 * remaining -> status. Derived from the current date on every call, so it stays correct
 * without a schedule update.
 *
 * `none` is the "not monitored" answer, which covers both a schedule that is inactive and
 * one with no usable dates: it renders as N/A and can never be highlighted, so an excluded
 * schedule cannot reach the expiring/expired states anywhere in the dashboard.
 */
export function getScheduleExpiration(schedule: any, timezone?: string): ScheduleExpiration {
  const not_monitored: ScheduleExpiration = { end_date: null, days_remaining: null, status: 'none' };
  if (!isScheduleActive(schedule)) return not_monitored;

  const end_date = getScheduleEndDate(schedule && schedule.curves);
  if (!end_date) return not_monitored;

  const days_remaining = momentTZ(end_date, DATE_FORMAT).diff(getProjectToday(timezone), 'days');
  const status: ScheduleExpirationStatus = days_remaining < 0
    ? 'expired'
    : days_remaining <= EXPIRATION_WARNING_DAYS ? 'expiring_soon' : 'normal';

  return { end_date, days_remaining, status };
}

export function getTimeRemainingLabel(expiration: ScheduleExpiration): string {
  if (expiration.status === 'none') return i18n.t('N/A') as string;
  if (expiration.status === 'expired') return i18n.t('Ended - Renewal Required') as string;
  if (expiration.days_remaining === 0) return i18n.t('Today') as string;

  const unit = expiration.days_remaining === 1 ? i18n.t('day') : i18n.t('days');
  return `${expiration.days_remaining} ${unit}`;
}

export function formatScheduleEndDate(end_date: string): string {
  return momentTZ(end_date, DATE_FORMAT).format('DD/MM/YYYY');
}

/**
 * Active schedules with 0-7 days remaining, soonest first. Expired schedules are excluded,
 * and so are inactive ones - `getScheduleExpiration` never reports them as expiring.
 */
export function getExpiringSchedules(schedules: any[], timezone?: string): ExpiringSchedule[] {
  return (Array.isArray(schedules) ? schedules : [])
    .map((schedule) => ({
      id: schedule.id,
      name: schedule.name,
      expiration: getScheduleExpiration(schedule, timezone)
    }))
    .filter((schedule) => schedule.expiration.status === 'expiring_soon')
    .sort((schedule1, schedule2) =>
      (schedule1.expiration.days_remaining as number) - (schedule2.expiration.days_remaining as number)
    );
}
